// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Lustre — the rules, as pure functions over a plain state. No DOM, no clock, no network, which is
// what lets the bot, the balance sim, the mechanism audit and the netcode all drive the SAME code.
//
// THE ONE STRUCTURAL DECISION: an action packs into a single int whose HIGH bits are the PLY index.
// Sorting the log ascending is therefore the canonical order, the acting seat is DERIVED by replay
// rather than carried on the wire, and the whole state is a pure function
// `replay(mode, seed, n, log)`. Two consequences:
//
//   1. There is no host. Every peer holds the same authority, host transfer is a non-event, and a
//      merge of two peers' logs is a sorted union rather than a patch that could diverge — there is
//      no incremental-apply path, because there is no incremental apply.
//   2. A peer that joins mid-round is instantly in sync from any single message.
//
// This is only available because LUSTRE HAS NO HIDDEN INFORMATION: both the deck order and every
// face-up card come from the shared seed, and nothing is ever held in a private hand. A received
// action that is not in the local legal set therefore means the two peers are running different
// RULES — a stale cached build — and it is reported as exactly that rather than silently applied.
//
// Discards and payments are CANONICALISED here rather than chosen in the UI. That is a deliberate
// narrowing: it drops the branching factor by two orders of magnitude, keeps the wire arg small, and
// deletes the whole class of "two peers computed a different split" desyncs. The player still sees
// the exact split on the action button before committing.

import { makeRng, shuffle } from '@ben-gy/game-engine/rng';
import {
  HAND_CAP, HUE_COUNT, LODE_SLOTS, OPAL_POOL, PIP_CAP, POOL_PER_HUE, SHELF_CAP,
  TAKE2_MIN, TIERS, TIER_COUNT, buildDeck, buildPatrons, type PatronDef, type SparDef,
} from './cards';
import { modeOf, stakeFor, type Mode, type ModeId } from './modes';

/** Bump when the wire meaning of an action, the state, or the scoring changes. */
export const RULES_REV = 1;

// ── the price of cutting ────────────────────────────────────────────────────────────────────────

/**
 * How much banked Lustre moves the dial one band. Narrowed below `3 × P` deliberately: at `3P` a
 * table taking turns produces the IDENTICAL price schedule for every seat, and the commons becomes
 * decoration. tests/mechanism.test.ts measures that directly as the k-th-cut price variance.
 */
export const bandValue = (players: number, mult = 3): number => mult * Math.max(2, players - 1);

/**
 * The band ladder. A TOTAL function, never a literal array — an array runs out, returns `undefined`,
 * becomes NaN inside a tick cost, and desyncs two peers silently while every test stays green.
 */
export function stepPrice(band: number, tail = 2): number {
  const b = band < 0 ? 0 : Math.floor(band);
  return b <= 6 ? b + 2 : 8 + tail * (b - 6);
}

/** Today's price, in ticks, for anyone at this table. Public, and the same for everyone. */
export function cutPrice(s: State): number {
  return stepPrice(Math.floor(s.dial / bandOf(s)), s.mode.tailStep);
}

/** This table's Lustre-per-band. Read from the mode so the sweep has exactly one knob to turn. */
export const bandOf = (s: State): number => bandValue(s.n, s.mode.bandMult);

// ── state ───────────────────────────────────────────────────────────────────────────────────────

export interface Spar {
  tier: number;
  hue: number;
  cost: number[];
  /** A cut spar has left the wheel forever: no pips, no bite, and it frees a rough slot. */
  cut: boolean;
  /** Commission: burnt to fill a Patron. Same as cut, but it scores nothing. */
  spent: boolean;
}

export interface Player {
  /** Position on the Wheel. `close` means retired. */
  pawn: number;
  /** Globally monotone stamp, taken every time the pawn lands. Breaks ties at equal pawns. */
  arrival: number;
  grit: number[];
  opals: number;
  bench: Spar[];
  shelf: Spar[];
  /** Patron ids held. Core/Dry allow one; Commission has no cap. */
  patrons: number[];
  /** Banked cut value only. The Patron is added at scoring, and never moves the dial in core/dry. */
  lustre: number;
  cuts: number;
  gathers: number;
  pans: number;
}

export interface State {
  mode: Mode;
  seed: number;
  n: number;
  close: number;
  players: Player[];
  pool: number[];
  opalPool: number;
  tailings: number[];
  /** Remaining draw pile per tier, in seeded order. */
  decks: SparDef[][];
  /** Face-up rows: `lode[tier][slot]`, null when that slot has gone dark. */
  lode: Array<Array<SparDef | null>>;
  /** Face-up Patron ids; -1 once taken. */
  patronsUp: number[];
  patronDefs: PatronDef[];
  /** Total value banked by the WHOLE TABLE — the commons the price is read off. */
  dial: number;
  ply: number;
  arrivalSeq: number;
  over: boolean;
}

export type EventKind = 'gather' | 'claim' | 'shelve' | 'cut' | 'pan' | 'patron' | 'retire';

export interface PlyEvent {
  kind: EventKind;
  seat: number;
  /** The tick the pawn stood on BEFORE the action. */
  tick: number;
  /** Ticks the action cost. */
  cost: number;
  /** Cut/Patron: the value banked. Claim: the tier. Gather: grit taken. */
  value: number;
  /** Cut/Patron: the dial reading the price was charged against, BEFORE this action. */
  dialBefore: number;
  /** Claim: total grit+opals paid. */
  paid: number;
}

const HUES_ARRAY = [0, 1, 2, 3];

export function createState(modeId: ModeId | string, seed: number, n: number): State {
  const mode = modeOf(modeId);
  const players = Math.max(2, Math.min(4, Math.floor(n)));
  const rng = makeRng((seed >>> 0) ^ 0x5ca1ab1e);
  const printed = buildDeck();
  const decks = printed.map((tier) => shuffle(rng, tier));
  const patronDefs = buildPatrons();
  const patronOrder = shuffle(
    rng,
    patronDefs.map((_, i) => i),
  );

  const s: State = {
    mode,
    seed: seed >>> 0,
    n: players,
    close: mode.close,
    players: [],
    pool: HUES_ARRAY.map(() => POOL_PER_HUE[players]),
    opalPool: OPAL_POOL[players],
    tailings: [0, 0, 0, 0],
    decks,
    lode: [[], [], []],
    patronsUp: patronOrder.slice(0, mode.patronsUp(players)),
    patronDefs,
    dial: 0,
    ply: 0,
    arrivalSeq: 0,
    over: false,
  };
  s.arrivalSeq = players;

  /**
   * Who acts first at equal pawns, decided by the SEED rather than by the seat index.
   *
   * MEASURED, and it was the largest fairness defect in the game. Every verb but CUT costs two
   * ticks, so pawns stay level and the tie-break resolves the same way all game: seat 0 got first
   * refusal on every card that appeared, forever. At two players it won 62% of games and outscored
   * seat 1 by 1.35 (SE 0.29). Dealing the order off the shared seed keeps the first-actor edge —
   * it is a real and legible part of a race — while detaching it from a seat index, so no player is
   * favoured by where they happen to sit. The residual edge is measured on its own terms in
   * tests/balance.test.ts, and a multi-round match rotates it.
   */
  const order = shuffle(
    rng,
    Array.from({ length: players }, (_, i) => i),
  );

  for (let seat = 0; seat < players; seat++) {
    const place = order.indexOf(seat);
    // The stake: whoever waits longest for their first pick is handed the most grit to wait with.
    //
    // Normalised by player count, because a flat per-place amount is three times as strong at four
    // players as at two — swept as a single constant it fixed 4P and left 2P at a 62% first-actor
    // win rate, or fixed 2P and overshot 4P to 13% against a fair 25%. Scaling so the LAST actor
    // always receives the same compensation is what made one number fit every count.
    const stake = [0, 0, 0, 0];
    const chips = Math.round((place * 2 * stakeFor(mode, players)) / Math.max(1, players - 1));
    for (let k = 0; k < chips; k++) {
      const hue = (seat + k) % HUE_COUNT;
      if (s.pool[hue] > 0) {
        s.pool[hue]--;
        stake[hue]++;
      }
    }
    s.players.push({
      pawn: 0,
      arrival: place,
      grit: stake,
      opals: 0,
      bench: [],
      shelf: [],
      patrons: [],
      lustre: 0,
      cuts: 0,
      gathers: 0,
      pans: 0,
    });
  }

  for (let t = 0; t < TIER_COUNT; t++) {
    for (let k = 0; k < LODE_SLOTS; k++) s.lode[t].push(null);
    refill(s, t);
  }
  return s;
}

/**
 * Refill a row. Tier exhaustion backfills DOWNWARD from the deeper tiers, so the cheap on-ramp is
 * the LAST thing to go dark rather than the first — a table that has bought out the Rough row must
 * not be left with nothing a new engine can afford.
 */
function refill(s: State, tier: number): void {
  for (let k = 0; k < LODE_SLOTS; k++) {
    if (s.lode[tier][k] !== null) continue;
    let card = s.decks[tier].pop();
    if (card === undefined) {
      for (let deeper = tier + 1; deeper < TIER_COUNT && card === undefined; deeper++) {
        card = s.decks[deeper].pop();
      }
    }
    s.lode[tier][k] = card ?? null;
  }
}

// ── derived reads ───────────────────────────────────────────────────────────────────────────────

export const isRough = (sp: Spar): boolean => !sp.cut && !sp.spent;

export function roughCount(p: Player): number {
  let n = 0;
  for (const sp of p.bench) if (isRough(sp)) n++;
  return n;
}

/** Pips per hue, capped. A fifth Garnet spar still drives the wheel; it grants no fifth pip. */
export function pips(p: Player): number[] {
  const out = [0, 0, 0, 0];
  for (const sp of p.bench) if (isRough(sp) && out[sp.hue] < PIP_CAP) out[sp.hue]++;
  return out;
}

/** How hard the wheel turns. Sets GATHER's YIELD — never its speed. */
export function bite(p: Player): number {
  let b = 0;
  for (const sp of p.bench) if (isRough(sp)) b += TIERS[sp.tier].bite;
  return b;
}

/** Grit taken by a GATHER spread, by Bite. Stepped, bounded, and public. */
export function yieldOf(b: number): number {
  return b >= 12 ? 5 : b >= 6 ? 4 : 3;
}

export const handSize = (p: Player): number => p.grit[0] + p.grit[1] + p.grit[2] + p.grit[3] + p.opals;

export const retired = (s: State, seat: number): boolean => s.players[seat].pawn >= s.close;

/**
 * Whose turn it is: the pawn furthest back, ties to whoever landed first. Returns -1 when everyone
 * has retired, which is the only ending.
 */
export function actingSeat(s: State): number {
  let best = -1;
  for (let seat = 0; seat < s.n; seat++) {
    if (retired(s, seat)) continue;
    if (best < 0) {
      best = seat;
      continue;
    }
    const a = s.players[seat];
    const b = s.players[best];
    if (a.pawn < b.pawn || (a.pawn === b.pawn && a.arrival < b.arrival)) best = seat;
  }
  return best;
}

// ── payment, canonicalised ──────────────────────────────────────────────────────────────────────

export interface Payment {
  /** Grit spent, per hue. */
  grit: number[];
  opals: number;
  total: number;
}

/**
 * What a spar actually costs this player, and exactly which chips pay for it.
 *
 * Canonical: grit first, in hue order; Opals only to cover a shortfall. The handling rule floors
 * every Claim at one chip even at a full discount — an entirely free card would make a big bench a
 * pure vending machine, which is the snowball this game exists to avoid.
 *
 * Returns null when the player cannot pay.
 */
export function payFor(p: Player, card: SparDef, pip = pips(p)): Payment | null {
  const grit = [0, 0, 0, 0];
  let opals = 0;
  let total = 0;
  for (const h of HUES_ARRAY) {
    const owed = Math.max(0, card.cost[h] - pip[h]);
    if (owed === 0) continue;
    const fromGrit = Math.min(owed, p.grit[h]);
    grit[h] = fromGrit;
    const short = owed - fromGrit;
    if (short > 0) opals += short;
    total += owed;
  }
  if (opals > p.opals) return null;

  if (total === 0) {
    // The handling rule. Pay from the largest holding, else an Opal.
    let bestHue = -1;
    for (const h of HUES_ARRAY) if (p.grit[h] > 0 && (bestHue < 0 || p.grit[h] > p.grit[bestHue])) bestHue = h;
    if (bestHue >= 0) {
      grit[bestHue] = 1;
      return { grit, opals: 0, total: 1 };
    }
    if (p.opals > 0) return { grit, opals: 1, total: 1 };
    return null;
  }
  return { grit, opals, total };
}

/**
 * Which chips are dropped to get back to the hand cap. Canonical: the largest holding first, Opals
 * never — an Opal is worth more than any single grit and dropping one is never right.
 */
export function discardPlan(p: Player): number[] {
  const drop = [0, 0, 0, 0];
  let over = handSize(p) - HAND_CAP;
  if (over <= 0) return drop;
  const held = p.grit.slice();
  while (over > 0) {
    let bestHue = -1;
    for (const h of HUES_ARRAY) if (held[h] > 0 && (bestHue < 0 || held[h] > held[bestHue])) bestHue = h;
    if (bestHue < 0) break;
    held[bestHue]--;
    drop[bestHue]++;
    over--;
  }
  return drop;
}

// ── Patrons ─────────────────────────────────────────────────────────────────────────────────────

/** What this mode actually asks for. Commission halves the bar; the card is otherwise identical. */
export function patronNeed(s: State, patronId: number): number[] {
  const def = s.patronDefs[patronId];
  if (!def) return [0, 0, 0, 0];
  const d = s.mode.patronDiv;
  return d === 1 ? def.need : def.need.map((v) => Math.ceil(v / d));
}

export function patronReady(s: State, seat: number, patronId: number, pip = pips(s.players[seat])): boolean {
  const def = s.patronDefs[patronId];
  if (!def) return false;
  const need = patronNeed(s, patronId);
  for (const h of HUES_ARRAY) if (pip[h] < need[h]) return false;
  return true;
}

/** Face-up Patrons this seat could take right now. */
export function readyPatrons(s: State, seat: number, pip = pips(s.players[seat])): number[] {
  const p = s.players[seat];
  if (p.patrons.length >= s.mode.patronCap) return [];
  const out: number[] = [];
  for (let i = 0; i < s.patronsUp.length; i++) {
    const id = s.patronsUp[i];
    if (id < 0) continue;
    if (patronReady(s, seat, id, pip)) out.push(i);
  }
  return out;
}

// ── action packing ──────────────────────────────────────────────────────────────────────────────

export const V_GATHER = 0;
export const V_CLAIM = 1;
export const V_SHELVE = 2;
export const V_CUT = 3;
export const V_PAN = 4;
export const V_PATRON = 5;
export const V_RETIRE = 6;

export const VERB_NAME = ['Gather', 'Claim', 'Shelve', 'Cut', 'Pan', 'Patron', 'Retire'];

/** 15 = no Patron rider. */
export const NO_PATRON = 15;

export const pack = (ply: number, verb: number, arg: number, patron = NO_PATRON): number =>
  ((ply << 22) | (verb << 19) | (patron << 15) | arg) >>> 0;

export const aPly = (a: number): number => (a >>> 22) & 255;
export const aVerb = (a: number): number => (a >>> 19) & 7;
export const aPatron = (a: number): number => (a >>> 15) & 15;
export const aArg = (a: number): number => a & 0x7fff;

// GATHER arg fields.
export const gTake2 = (arg: number): boolean => (arg & 1) === 1;
export const gTake2Hue = (arg: number): number => (arg >>> 1) & 3;
export const gMask = (arg: number): number => (arg >>> 3) & 15;
export const gRep = (arg: number, i: number): number => ((arg >>> (7 + i * 3)) & 7) - 1;
export const packGather = (mask: number, reps: number[]): number =>
  (mask << 3) | ((reps[0] !== undefined ? reps[0] + 1 : 0) << 7) | ((reps[1] !== undefined ? reps[1] + 1 : 0) << 10);
export const packTake2 = (hue: number): number => 1 | (hue << 1);

// CLAIM arg fields.
export const cFromShelf = (arg: number): boolean => (arg & 1) === 1;
export const cTier = (arg: number): number => (arg >>> 1) & 3;
export const cSlot = (arg: number): number => (arg >>> 3) & 3;
export const packClaimLode = (tier: number, slot: number): number => (tier << 1) | (slot << 3);
export const packClaimShelf = (idx: number): number => 1 | (idx << 3);

// SHELVE arg fields.
export const sTier = (arg: number): number => arg & 3;
export const sSlot = (arg: number): number => (arg >>> 2) & 3;
export const sOpal = (arg: number): boolean => ((arg >>> 4) & 1) === 1;
export const packShelve = (tier: number, slot: number, opal: boolean): number =>
  tier | (slot << 2) | (opal ? 16 : 0);

// PAN arg fields: two independent picks.
export const panHue = (arg: number, i: number): number => ((arg >>> (i * 3)) & 7) - 1;
export const packPan = (hues: number[]): number =>
  ((hues[0] !== undefined ? hues[0] + 1 : 0)) | ((hues[1] !== undefined ? hues[1] + 1 : 0) << 3);

// ── legality ────────────────────────────────────────────────────────────────────────────────────

const combos3 = (items: number[]): number[][] => {
  const out: number[][] = [];
  const k = Math.min(3, items.length);
  const rec = (start: number, acc: number[]): void => {
    if (acc.length === k) {
      out.push(acc.slice());
      return;
    }
    for (let i = start; i < items.length; i++) {
      acc.push(items[i]);
      rec(i + 1, acc);
      acc.pop();
    }
  };
  rec(0, []);
  return out;
};

/** Multisets of size r drawn from `items` — the repeat picks a big Bite earns. */
const multisets = (items: number[], r: number): number[][] => {
  if (r === 0) return [[]];
  const out: number[][] = [];
  const rec = (start: number, acc: number[]): void => {
    if (acc.length === r) {
      out.push(acc.slice());
      return;
    }
    for (let i = start; i < items.length; i++) {
      acc.push(items[i]);
      rec(i, acc);
      acc.pop();
    }
  };
  rec(0, []);
  return out;
};

/** Base actions, without Patron riders. The rider expansion is layered on in `legalActions`. */
function baseActions(s: State): number[] {
  const seat = actingSeat(s);
  if (seat < 0) return [];
  const p = s.players[seat];
  const ply = s.ply;
  const room = s.close - p.pawn;
  const out: number[] = [];

  // GATHER — 2 ticks, always, for every player in every state. Bite buys yield, never speed.
  if (room >= 2) {
    const nonEmpty = HUES_ARRAY.filter((h) => s.pool[h] > 0);
    if (nonEmpty.length > 0) {
      const reps = yieldOf(bite(p)) - 3;
      for (const spread of combos3(nonEmpty)) {
        let mask = 0;
        for (const h of spread) mask |= 1 << h;
        for (const rep of multisets(nonEmpty, reps)) {
          // The whole draw must fit the pool, spread AND repeats together. Checking each half
          // against the pool separately lets a repeat take from a pile the spread just emptied,
          // which drives that pile negative — found by the cap invariant in tests/mechanism.test.ts,
          // never by a balance number, because a negative pile still produces plausible scores.
          const want = [0, 0, 0, 0];
          for (const h of spread) want[h]++;
          for (const h of rep) want[h]++;
          if (HUES_ARRAY.some((h) => want[h] > s.pool[h])) continue;
          out.push(pack(ply, V_GATHER, packGather(mask, rep)));
        }
      }
    }
    for (const h of HUES_ARRAY) if (s.pool[h] >= TAKE2_MIN) out.push(pack(ply, V_GATHER, packTake2(h)));
  }

  // CLAIM — 2 ticks. Illegal at the rough cap, which is a common and confusing late state, so the
  // UI says so rather than simply refusing.
  if (room >= 2 && roughCount(p) < s.mode.roughCap) {
    const pip = pips(p);
    for (let t = 0; t < TIER_COUNT; t++) {
      for (let k = 0; k < LODE_SLOTS; k++) {
        const card = s.lode[t][k];
        if (card && payFor(p, card, pip)) out.push(pack(ply, V_CLAIM, packClaimLode(t, k)));
      }
    }
    for (let i = 0; i < p.shelf.length; i++) {
      if (payFor(p, p.shelf[i], pip)) out.push(pack(ply, V_CLAIM, packClaimShelf(i)));
    }
  }

  // SHELVE — 2 ticks. The Opal rider only comes off the Bright or Deep rows, and is optional.
  if (room >= 2 && p.shelf.length < SHELF_CAP) {
    for (let t = 0; t < TIER_COUNT; t++) {
      for (let k = 0; k < LODE_SLOTS; k++) {
        if (!s.lode[t][k]) continue;
        out.push(pack(ply, V_SHELVE, packShelve(t, k, false)));
        if (t > 0 && s.opalPool > 0) out.push(pack(ply, V_SHELVE, packShelve(t, k, true)));
      }
    }
  }

  // CUT — the band price. The only tick cost that varies with anything, and it only ever rises.
  if (!s.mode.patronIsVerb) {
    const price = cutPrice(s);
    if (room >= price) {
      for (let i = 0; i < p.bench.length; i++) if (isRough(p.bench[i])) out.push(pack(ply, V_CUT, i));
    }
  }

  // PAN — Dry Lode. Flat 2 ticks: your engine buys you nothing on the spoil heap.
  if (s.mode.pan && room >= 2) {
    const avail = HUES_ARRAY.filter((h) => s.tailings[h] > 0);
    if (avail.length > 0) {
      for (const picks of multisets(avail, Math.min(2, s.tailings[0] + s.tailings[1] + s.tailings[2] + s.tailings[3]))) {
        if (!affordableTailings(s, picks)) continue;
        out.push(pack(ply, V_PAN, packPan(picks)));
      }
    }
  }

  // PATRON — a verb only in Commission, where it costs the band price and burns two rough spars.
  if (s.mode.patronIsVerb) {
    const price = cutPrice(s);
    if (room >= price && roughCount(p) >= 2) {
      for (const idx of readyPatrons(s, seat)) out.push(pack(ply, V_PATRON, idx));
    }
  }

  return out;
}

const affordableTailings = (s: State, picks: number[]): boolean => {
  const need = [0, 0, 0, 0];
  for (const h of picks) need[h]++;
  return HUES_ARRAY.every((h) => need[h] <= s.tailings[h]);
};

/**
 * Every legal action for the seat that is on turn, Patron riders expanded.
 *
 * In Open Wheel and Dry Lode taking a Patron is NOT a verb — a zero-tick verb would let the furthest
 * back seat take Patrons consecutively for free — it rides on whichever action earns it. Cutting
 * destroys pips, so a Patron must be taken before you start converting, and that ordering constraint
 * is the Patron's entire job.
 */
export function legalActions(s: State): number[] {
  const seat = actingSeat(s);
  if (seat < 0 || s.over) return [];
  const base = baseActions(s);
  if (base.length === 0) return [pack(s.ply, V_RETIRE, 0)];
  if (s.mode.patronIsVerb) return base;

  const out: number[] = [];
  for (const a of base) {
    out.push(a);
    for (const idx of patronRidersFor(s, seat, a)) out.push(pack(s.ply, aVerb(a), aArg(a), idx));
  }
  return out;
}

/**
 * Which Patron riders this action could carry.
 *
 * Only CLAIM changes pips, so for every other verb the answer is simply "whoever you already
 * qualify for" — computed without cloning the state, because the bot asks this on every ply of
 * every simulated game.
 */
function patronRidersFor(s: State, seat: number, a: number): number[] {
  const p = s.players[seat];
  if (p.patrons.length >= s.mode.patronCap) return [];
  const pip = pips(p);
  if (aVerb(a) === V_CLAIM) {
    const card = claimTarget(s, seat, aArg(a));
    if (card && pip[card.hue] < PIP_CAP) pip[card.hue]++;
  }
  const out: number[] = [];
  for (let i = 0; i < s.patronsUp.length; i++) {
    const id = s.patronsUp[i];
    if (id >= 0 && patronReady(s, seat, id, pip)) out.push(i);
  }
  return out;
}

function claimTarget(s: State, seat: number, arg: number): SparDef | null {
  if (cFromShelf(arg)) return s.players[seat].shelf[cSlot(arg)] ?? null;
  return s.lode[cTier(arg)]?.[cSlot(arg)] ?? null;
}

export const isLegal = (s: State, a: number): boolean => legalActions(s).includes(a >>> 0);

// ── applying one ply ────────────────────────────────────────────────────────────────────────────

const toSpar = (d: SparDef): Spar => ({ tier: d.tier, hue: d.hue, cost: d.cost.slice(), cut: false, spent: false });

/**
 * Apply one action. Returns false if it is not legal here, which the caller must treat as a BUILD
 * mismatch — the two peers are not running the same rules — and never as a recoverable desync.
 */
export function applyPly(s: State, action: number, events: PlyEvent[] = []): boolean {
  const a = action >>> 0;
  if (s.over) return false;
  if (aPly(a) !== s.ply) return false;
  const seat = actingSeat(s);
  if (seat < 0) return false;
  if (!legalActions(s).includes(a)) return false;

  const p = s.players[seat];
  const verb = aVerb(a);
  const arg = aArg(a);
  const tick = p.pawn;
  const dialBefore = s.dial;
  let cost = 2;
  let value = 0;
  let paid = 0;

  switch (verb) {
    case V_GATHER: {
      if (gTake2(arg)) {
        const h = gTake2Hue(arg);
        s.pool[h] -= 2;
        p.grit[h] += 2;
        value = 2;
      } else {
        const mask = gMask(arg);
        for (const h of HUES_ARRAY) {
          if ((mask >> h) & 1) {
            s.pool[h]--;
            p.grit[h]++;
            value++;
          }
        }
        for (let i = 0; i < 2; i++) {
          const h = gRep(arg, i);
          if (h < 0) continue;
          s.pool[h]--;
          p.grit[h]++;
          value++;
        }
      }
      p.gathers++;
      const drop = discardPlan(p);
      for (const h of HUES_ARRAY) {
        p.grit[h] -= drop[h];
        s.pool[h] += drop[h];
      }
      break;
    }
    case V_CLAIM: {
      const fromShelf = cFromShelf(arg);
      const card = claimTarget(s, seat, arg)!;
      const pay = payFor(p, card)!;
      for (const h of HUES_ARRAY) {
        p.grit[h] -= pay.grit[h];
        // Dry Lode: the first grit of the payment is destroyed, the remainder returns to the pool.
        // Destroying the whole payment empties a four-player pool inside four claims.
        s.pool[h] += pay.grit[h];
      }
      p.opals -= pay.opals;
      s.opalPool += pay.opals;
      if (s.mode.destroys && pay.total > 0) {
        let taken = false;
        for (const h of HUES_ARRAY) {
          if (!taken && pay.grit[h] > 0) {
            s.pool[h]--;
            s.tailings[h]++;
            taken = true;
          }
        }
      }
      paid = pay.total;
      value = card.tier;
      p.bench.push(toSpar(card));
      if (fromShelf) p.shelf.splice(cSlot(arg), 1);
      else {
        s.lode[cTier(arg)][cSlot(arg)] = null;
        refill(s, cTier(arg));
      }
      break;
    }
    case V_SHELVE: {
      const t = sTier(arg);
      const k = sSlot(arg);
      const card = s.lode[t][k]!;
      p.shelf.push(toSpar(card));
      s.lode[t][k] = null;
      refill(s, t);
      if (sOpal(arg)) {
        s.opalPool--;
        p.opals++;
      }
      const drop = discardPlan(p);
      for (const h of HUES_ARRAY) {
        p.grit[h] -= drop[h];
        s.pool[h] += drop[h];
      }
      break;
    }
    case V_CUT: {
      const spar = p.bench[arg];
      cost = cutPrice(s);
      value = TIERS[spar.tier].cut;
      spar.cut = true;
      p.lustre += value;
      p.cuts++;
      // The spar's cut value IS its dial cost. One number, both jobs — which is what makes the
      // decision thumb-computable, and what keeps the commons cost per point flat across tiers.
      s.dial += value;
      break;
    }
    case V_PAN: {
      for (let i = 0; i < 2; i++) {
        const h = panHue(arg, i);
        if (h < 0) continue;
        s.tailings[h]--;
        p.grit[h]++;
        value++;
      }
      p.pans++;
      const drop = discardPlan(p);
      for (const h of HUES_ARRAY) {
        p.grit[h] -= drop[h];
        s.pool[h] += drop[h];
      }
      break;
    }
    case V_PATRON: {
      // Commission only. Costs the band price AND burns two rough spars, so Loop A ("your engine is
      // your score") is present in the one mode that has no CUT verb.
      cost = cutPrice(s);
      const id = s.patronsUp[arg];
      const def = s.patronDefs[id];
      value = def.value;
      p.patrons.push(id);
      s.patronsUp[arg] = -1;
      const need = patronNeed(s, id);
      let burnt = 0;
      for (const sp of p.bench) {
        if (burnt >= 2) break;
        if (isRough(sp) && need[sp.hue] > 0) {
          sp.spent = true;
          burnt++;
        }
      }
      for (const sp of p.bench) {
        if (burnt >= 2) break;
        if (isRough(sp)) {
          sp.spent = true;
          burnt++;
        }
      }
      s.dial += value;
      break;
    }
    case V_RETIRE: {
      cost = s.close - p.pawn;
      break;
    }
    default:
      return false;
  }

  // The rider. Validated AFTER the action, because a Claim can be what qualifies you.
  const rider = aPatron(a);
  if (rider !== NO_PATRON && !s.mode.patronIsVerb) {
    const id = s.patronsUp[rider];
    p.patrons.push(id);
    s.patronsUp[rider] = -1;
    events.push({ kind: 'patron', seat, tick, cost: 0, value: s.patronDefs[id].value, dialBefore, paid: 0 });
  }

  p.pawn = Math.min(s.close, p.pawn + cost);
  p.arrival = s.arrivalSeq++;
  s.ply++;
  events.push({ kind: EVENT_OF[verb], seat, tick, cost, value, dialBefore, paid });

  if (actingSeat(s) < 0) s.over = true;
  return true;
}

const EVENT_OF: EventKind[] = ['gather', 'claim', 'shelve', 'cut', 'pan', 'patron', 'retire'];

// ── the log ─────────────────────────────────────────────────────────────────────────────────────

/**
 * The canonical order of a log. The PLY lives in the high bits, so a plain ascending sort IS the
 * order the rules resolve in — which is why two peers holding the same SET of actions necessarily
 * hold the same state, whatever order the datagrams arrived in.
 *
 * Two peers can both fill in for a seat that went quiet. The fill-in is the deterministic shipped
 * bot so the actions are normally byte-identical; when they are not, the smaller wins on both peers.
 */
export function canonical(log: number[]): number[] {
  const byPly = new Map<number, number>();
  for (const raw of log) {
    const a = raw >>> 0;
    const k = aPly(a);
    const cur = byPly.get(k);
    if (cur === undefined || a < cur) byPly.set(k, a);
  }
  return [...byPly.values()].sort((x, y) => x - y);
}

/**
 * A deep-enough copy for a candidate trial. The decks and the printed Patron table are immutable
 * once dealt apart from `pop()`, so the deck arrays are copied and the card objects shared.
 */
export function cloneState(s: State): State {
  return {
    mode: s.mode,
    seed: s.seed,
    n: s.n,
    close: s.close,
    pool: s.pool.slice(),
    opalPool: s.opalPool,
    tailings: s.tailings.slice(),
    decks: [s.decks[0].slice(), s.decks[1].slice(), s.decks[2].slice()],
    lode: [s.lode[0].slice(), s.lode[1].slice(), s.lode[2].slice()],
    patronsUp: s.patronsUp.slice(),
    patronDefs: s.patronDefs,
    dial: s.dial,
    ply: s.ply,
    arrivalSeq: s.arrivalSeq,
    over: s.over,
    players: s.players.map((p) => ({
      pawn: p.pawn,
      arrival: p.arrival,
      grit: p.grit.slice(),
      opals: p.opals,
      bench: p.bench.map((sp) => ({ tier: sp.tier, hue: sp.hue, cost: sp.cost, cut: sp.cut, spent: sp.spent })),
      shelf: p.shelf.map((sp) => ({ tier: sp.tier, hue: sp.hue, cost: sp.cost, cut: sp.cut, spent: sp.spent })),
      patrons: p.patrons.slice(),
      lustre: p.lustre,
      cuts: p.cuts,
      gathers: p.gathers,
      pans: p.pans,
    })),
  };
}

export interface ReplayResult {
  state: State;
  /** The first action that was not legal — a build mismatch, not a desync we can recover from. */
  bad: number | null;
  events: PlyEvent[];
}

/** The whole state as a pure function of (mode, seed, players, log). There is no other path in. */
export function replay(modeId: ModeId | string, seed: number, n: number, log: number[]): ReplayResult {
  const state = createState(modeId, seed, n);
  const events: PlyEvent[] = [];
  for (const a of canonical(log)) {
    if (state.over) break;
    if (!applyPly(state, a, events)) return { state, bad: a, events };
  }
  return { state, bad: null, events };
}

/** FNV-1a over everything a peer must agree on. Cheap, and it catches a rules drift immediately. */
export function digest(s: State): number {
  let h = 0x811c9dc5;
  const mix = (v: number): void => {
    h ^= v & 0xff;
    h = Math.imul(h, 0x01000193) >>> 0;
    h ^= (v >>> 8) & 0xff;
    h = Math.imul(h, 0x01000193) >>> 0;
  };
  mix(s.ply);
  mix(s.dial);
  mix(s.over ? 1 : 0);
  for (const v of s.pool) mix(v);
  mix(s.opalPool);
  for (const v of s.tailings) mix(v);
  for (const row of s.lode) for (const c of row) mix(c ? c.tier * 4 + c.hue + 1 : 0);
  for (const id of s.patronsUp) mix(id + 2);
  for (const p of s.players) {
    mix(p.pawn);
    mix(p.arrival);
    for (const v of p.grit) mix(v);
    mix(p.opals);
    mix(p.lustre);
    mix(p.cuts);
    for (const sp of p.bench) mix(sp.tier * 16 + sp.hue * 4 + (sp.cut ? 1 : 0) + (sp.spent ? 2 : 0));
    for (const sp of p.shelf) mix(sp.tier * 4 + sp.hue + 1);
    for (const id of p.patrons) mix(id + 2);
  }
  return h >>> 0;
}

// ── scoring ─────────────────────────────────────────────────────────────────────────────────────

export interface Breakdown {
  seat: number;
  /** Banked cut value. In Commission this is always 0. */
  cut: number;
  patron: number;
  total: number;
  cuts: number;
  rough: number;
  /** Rough spars still on the bench at the Close. Worth exactly nothing, and that is the game. */
  stranded: number;
  bite: number;
  grit: number;
  gathers: number;
  pans: number;
  patrons: number[];
}

export function score(s: State, seat: number): Breakdown {
  const p = s.players[seat];
  let patron = 0;
  for (const id of p.patrons) patron += s.patronDefs[id].value;
  const rough = roughCount(p);
  return {
    seat,
    cut: p.lustre,
    patron,
    total: p.lustre + patron,
    cuts: p.cuts,
    rough,
    stranded: rough,
    bite: bite(p),
    grit: handSize(p),
    gathers: p.gathers,
    pans: p.pans,
    patrons: p.patrons.slice(),
  };
}

/** A deterministic, seed-symmetric last resort. Never the seat index, which favours seat 0 forever. */
export function tieHash(seed: number, seat: number): number {
  let h = (seed ^ 0x9e3779b9 ^ (seat * 0x85ebca6b)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x2545f491) >>> 0;
  return (h ^ (h >>> 15)) >>> 0;
}

export interface Standing extends Breakdown {
  rank: number;
}

/** Final standings, best first. EVERY seat, always — never just the local one. */
export function standings(s: State): Standing[] {
  const rows: Standing[] = [];
  for (let seat = 0; seat < s.n; seat++) rows.push({ ...score(s, seat), rank: 0 });
  rows.sort(
    (a, b) =>
      b.total - a.total ||
      a.cuts - b.cuts ||
      b.grit - a.grit ||
      tieHash(s.seed, a.seat) - tieHash(s.seed, b.seat),
  );
  let rank = 0;
  let last = -1;
  rows.forEach((r, i) => {
    if (r.total !== last) {
      rank = i + 1;
      last = r.total;
    }
    r.rank = rank;
  });
  return rows;
}

export const winners = (s: State): number[] => {
  const rows = standings(s);
  return rows.length === 0 ? [] : rows.filter((r) => r.rank === 1).map((r) => r.seat);
};
